import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * d-filters-drawer
 *
 * Envolve o conteúdo do drawer de filtros do celular (blocks/d-filters.liquid) e
 * troca a aplicação imediata por uma aplicação DIFERIDA:
 *
 *   • marcar/desmarcar uma opção (ou mexer no preço) só muda a SELEÇÃO;
 *   • "Aplicar filtros" é o único caminho que atualiza a vitrine e a URL;
 *   • "Limpar filtros" desmarca tudo, mas também só vale depois do Aplicar;
 *   • fechar sem aplicar (X, clique fora, Esc) devolve a seleção ao que está
 *     aplicado de fato, lido do HTML do servidor via `defaultChecked` /
 *     `defaultValue` (o morph mantém esses atributos em sincronia).
 *
 * Para isso o snippet `d-filters-groups` é renderizado com `deferred: true`
 * nesta instância, o que remove os `on:change` que chamariam `/updateFilters` e
 * `/updatePriceFilterAndResults` do facets.js. O painel do desktop continua
 * aplicando na hora e não passa por aqui.
 */
class DFiltersDrawer extends Component {
  /** Sinaliza que o fechamento veio do "Aplicar filtros" (não deve reverter). */
  #applying = false;

  /** @type {Element | null} */
  #dialogEl = null;

  connectedCallback() {
    super.connectedCallback();

    // Guardado na conexão: no disconnect a árvore já pode estar desmontada e o
    // `closest` devolveria null, deixando o listener pendurado.
    this.#dialogEl = this.closest('dialog-component');
    this.#dialogEl?.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.addEventListener('change', this.#onChange);

    this.#syncPending();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogEl?.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.removeEventListener('change', this.#onChange);
  }

  /** Re-sincroniza o rodapé e o contador depois de um morph da section. */
  updatedCallback() {
    super.updatedCallback();
    this.#syncPending();
  }

  /**
   * Aplica a seleção pendente e fecha o drawer.
   *
   * A ordem importa: o fetch da section é disparado primeiro (só aquece o cache
   * do section-renderer), depois esperamos a animação de fechamento e só então
   * chamamos `updateFilters`. Aplicar com o drawer ainda fechando faria o morph
   * passar por cima da classe `dialog-closing` do <dialog> e travar o
   * fechamento — e como o HTML já está em cache, a vitrine atualiza na hora.
   */
  applyFilters = async () => {
    if (this.#applying) return;

    const form = this.#form;
    this.#applying = true;

    this.#warmCache();
    await this.#closeDrawer();

    if (form && typeof form.updateFilters === 'function') form.updateFilters();

    this.#applying = false;
  };

  /**
   * Desmarca tudo sem aplicar: o cliente ainda precisa tocar em "Aplicar
   * filtros" para a vitrine mudar (ou fechar o drawer para desistir).
   */
  clearFilters = () => {
    for (const input of this.#checkboxes) input.checked = false;

    for (const slider of this.#priceSliders) {
      const floor = slider.getAttribute('data-floor') ?? '0';
      const ceil = slider.getAttribute('data-ceil') ?? '0';

      this.#setValue(slider.querySelector('[data-range-min]'), floor);
      this.#setValue(slider.querySelector('[data-range-max]'), ceil);
      for (const hidden of this.#hiddenPriceInputs(slider)) this.#setValue(hidden, '');

      this.#renderSlider(slider);
    }

    this.#syncPending();
  };

  /** Volta a seleção para o estado que está realmente aplicado. */
  #revertPending() {
    for (const input of this.#checkboxes) input.checked = input.defaultChecked;

    for (const slider of this.#priceSliders) {
      for (const input of slider.querySelectorAll('[data-range-min], [data-range-max]')) {
        this.#setValue(input, input.defaultValue);
      }
      for (const hidden of this.#hiddenPriceInputs(slider)) this.#setValue(hidden, hidden.defaultValue);

      this.#renderSlider(slider);
    }

    this.#syncPending();
  }

  #onChange = () => this.#syncPending();

  #onDialogClose = () => {
    if (this.#applying) return;
    this.#revertPending();
  };

  /**
   * Atualiza o contador do título e libera/bloqueia o "Limpar filtros" conforme
   * a seleção pendente — é o único retorno visual de que algo está selecionado
   * mas ainda não aplicado.
   */
  #syncPending() {
    const count = this.#pendingCount();

    const badge = this.querySelector('[data-drawer-count]');
    if (badge instanceof HTMLElement) {
      badge.textContent = `(${count})`;
      badge.hidden = count === 0;
    }

    const clear = this.querySelector('[data-drawer-clear]');
    if (clear instanceof HTMLButtonElement) clear.disabled = count === 0;
  }

  /** @returns {number} quantos filtros estão selecionados no drawer */
  #pendingCount() {
    let count = this.#checkboxes.filter((input) => input.checked).length;

    for (const slider of this.#priceSliders) {
      const floor = Number(slider.getAttribute('data-floor') ?? 0);
      const ceil = Number(slider.getAttribute('data-ceil') ?? 0);
      const lo = slider.querySelector('[data-range-min]');
      const hi = slider.querySelector('[data-range-max]');

      const loActive = lo instanceof HTMLInputElement && Number(lo.value) > floor;
      const hiActive = hi instanceof HTMLInputElement && Number(hi.value) < ceil;

      if (loActive || hiActive) count += 1;
    }

    return count;
  }

  /**
   * Busca o HTML da section com os filtros pendentes enquanto o drawer fecha,
   * para o `updateFilters` seguinte acertar o cache em vez de esperar a rede.
   * Falhar aqui não quebra nada: o pior caso é uma requisição desperdiçada.
   */
  #warmCache() {
    const form = this.#form;
    if (!form || typeof form.createURLParameters !== 'function') return;

    const sectionId = form.getAttribute('section-id');
    if (!sectionId) return;

    const url = new URL(window.location.pathname, window.location.origin);
    for (const [param, value] of form.createURLParameters()) url.searchParams.append(param, value);

    sectionRenderer.getSectionHTML(sectionId, true, url);
  }

  /** @returns {Promise<void>} */
  async #closeDrawer() {
    const dialog = this.#dialogEl;
    if (dialog && typeof dialog.closeDialog === 'function') await dialog.closeDialog();
  }

  get #form() {
    return this.querySelector('facets-form-component');
  }

  /** @returns {HTMLInputElement[]} */
  get #checkboxes() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[type="checkbox"][name]')]);
  }

  /** @returns {Element[]} */
  get #priceSliders() {
    return [...this.querySelectorAll('d-price-range')];
  }

  /**
   * Os inputs que vão no form são irmãos do slider, dentro do
   * price-facet-component (é de lá que os refs nativos minInput/maxInput saem).
   * @param {Element} slider
   * @returns {HTMLInputElement[]}
   */
  #hiddenPriceInputs(slider) {
    const facet = slider.closest('price-facet-component') ?? slider;
    return /** @type {HTMLInputElement[]} */ ([
      ...facet.querySelectorAll('[data-price-input-min], [data-price-input-max]'),
    ]);
  }

  /**
   * @param {Element | null} input
   * @param {string} value
   */
  #setValue(input, value) {
    if (input instanceof HTMLInputElement) input.value = value;
  }

  /** @param {Element} slider */
  #renderSlider(slider) {
    if ('render' in slider && typeof slider.render === 'function') slider.render();
  }
}

if (!customElements.get('d-filters-drawer')) {
  customElements.define('d-filters-drawer', DFiltersDrawer);
}
